import type { BimContractObjectData } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useBimCreateContractSubObject } from '../BimContractStructure/hooks/useBimCreateContractSubObject';
import type { ItemTableContractObject } from '../BimContractStructure/hooks/useBimItemsContractStructureTable';
import { useBimUpdateContractSubObject } from '../BimContractStructure/hooks/useBimUpdateContractSubObject';

interface IUseBimContractSubObject {
    onClose: () => void;
    contractObject?: ItemTableContractObject;
    parentContractObject?: ItemTableContractObject;
    isUseContract?: boolean;
    onSuccess: (item: BimContractObjectData) => void;
}

export const useBimContractSubObject = ({
    isUseContract,
    contractObject,
    parentContractObject,
    onClose,
    onSuccess,
}: IUseBimContractSubObject) => {
    const dispatch = useDispatch();

    const bimCreateContractSubObjectRequest = useBimCreateContractSubObject({ onClose, onSuccess });

    const bimUpdateContractSubObjectRequest = useBimUpdateContractSubObject({ onClose, onSuccess });

    const defaultValuesContractSubObject = useMemo(() => {
        if (isUseContract && contractObject) {
            return Object.entries(contractObject)?.reduce((acc: any, [key, value]) => {
                if (key === 'title') {
                    acc = { ...acc, objectName: value };
                }
                if (key === 'hasGuaranteedVolume') {
                    acc = { ...acc, hasGuaranteedVolume: value === 'Да' };
                }

                return acc;
            }, {});
        }

        return undefined;
    }, [contractObject, isUseContract]);

    const onSubmitContractSubObject = useCallback(
        (formData) => {
            if (defaultValuesContractSubObject) {
                dispatch(
                    bimUpdateContractSubObjectRequest({
                        contractObjectInput: {
                            name: formData.objectName,
                            id: contractObject?.id,
                            hasGuaranteedVolume: formData.hasGuaranteedVolume,
                        },
                    }),
                );
            } else if (parentContractObject) {
                dispatch(
                    bimCreateContractSubObjectRequest({
                        contractObjectInput: {
                            name: formData.objectName,
                            contractObjectId: parentContractObject?.id,
                            hasGuaranteedVolume: formData.hasGuaranteedVolume,
                        },
                    }),
                );
            }
        },
        [
            bimCreateContractSubObjectRequest,
            bimUpdateContractSubObjectRequest,
            contractObject?.id,
            defaultValuesContractSubObject,
            dispatch,
            parentContractObject,
        ],
    );

    return {
        defaultValuesContractSubObject,
        onSubmitContractSubObject,
    };
};
